// Shared admin building blocks, so every page reads the same: headers, tabs, status pills, drawers,
// dialogs (instead of window.confirm/prompt), toasts, empty/loading states and small formatters.
import {
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
  type ComponentType,
} from 'react';
import { Link } from 'react-router';
import { Check, Copy, Inbox, Lock, Search, TriangleAlert, UserX, X } from 'lucide-react';
import { PERMISSIONS, type Permission } from '@gaintplay/core/permissions';
import { useAssistantOpen } from '../lib/assistant';

/* ------------------------------------------------------------------ Page chrome */

export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  eyebrow?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-end gap-x-6 gap-y-4">
      {/* Grows and wraps its own text first, so actions stay beside the title when there's room. */}
      <div className="min-w-0 flex-[1_1_26rem]">
        {eyebrow && <div className="mb-1 text-[13px] font-semibold text-stone">{eyebrow}</div>}
        <h1 className="page-title">{title}</h1>
        {description && (
          <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-graphite">{description}</p>
        )}
      </div>
      {actions && (
        <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
          {actions}
        </div>
      )}
    </header>
  );
}

/** Marks a horizontal scroller with data-more=start|end|both so CSS (.scroll-cue) fades the edge
 *  that hides more content. */
export function useScrollCue<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      const start = el.scrollLeft > 2;
      const end = el.scrollLeft < max - 2;
      const v = max <= 2 ? '' : start && end ? 'both' : start ? 'start' : end ? 'end' : '';
      if (v) el.dataset.more = v;
      else delete el.dataset.more;
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    for (const c of Array.from(el.children)) ro.observe(c);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);
  return ref;
}

/** Sideways scroller for wide tables: scrolls inside its card and fades the clipped edge. */
export function ScrollX({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useScrollCue<HTMLDivElement>();
  return (
    <div ref={ref} className={`scroll-cue overflow-x-auto ${className}`}>
      {children}
    </div>
  );
}

/** Segmented filter: a sunken track with the active option lifted, counts as small badges. */
export function Tabs<K extends string>({
  value,
  onChange,
  items,
  label,
}: {
  value: K;
  onChange: (k: K) => void;
  items: { key: K; label: string; count?: number | undefined; hot?: boolean }[];
  /** What the tabs filter, for screen readers ("Status", "Who decided"). */
  label?: string;
}) {
  const ref = useScrollCue<HTMLDivElement>();
  const move = (e: React.KeyboardEvent, i: number) => {
    const n = items.length;
    const j =
      e.key === 'ArrowRight'
        ? (i + 1) % n
        : e.key === 'ArrowLeft'
          ? (i - 1 + n) % n
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? n - 1
              : -1;
    if (j < 0) return;
    e.preventDefault();
    onChange(items[j]!.key);
    ref.current?.querySelectorAll<HTMLButtonElement>('[role=tab]')[j]?.focus();
  };
  return (
    <div
      ref={ref}
      role="tablist"
      aria-label={label}
      className="scroll-cue no-scrollbar inline-flex max-w-full gap-0.5 overflow-x-auto rounded-[12px] bg-ink/[0.06] p-1"
    >
      {items.map((t, i) => {
        const on = t.key === value;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(t.key)}
            onKeyDown={(e) => move(e, i)}
            className={`flex min-h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[9px] px-3 py-1.5 text-[13px] font-semibold transition focus-visible:outline-2 focus-visible:-outline-offset-2 max-sm:min-h-10 ${
              on
                ? 'bg-paper text-ink shadow-[0_1px_0_#e2ddd2,0_1px_3px_rgba(17,17,16,0.08)]'
                : 'text-graphite hover:bg-paper/50 hover:text-ink'
            }`}
          >
            {t.label}
            {t.count !== undefined && t.count > 0 && (
              <span
                className={`grid h-[18px] min-w-[18px] place-items-center rounded-full px-1 text-[11px] font-bold tabular-nums ${
                  t.hot ? 'bg-coin text-ink' : on ? 'bg-ink text-white' : 'bg-ink/10 text-graphite'
                }`}
              >
                {t.count > 999 ? '999+' : t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder,
  className = '',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  className?: string;
}) {
  return (
    <label className={`relative block ${className}`}>
      <Search
        size={15}
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone"
      />
      <input
        className="input min-h-10 py-2 pl-9 pr-9 text-[14px]"
        placeholder={placeholder}
        aria-label={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && value) {
            e.stopPropagation();
            onChange('');
          }
        }}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute right-1.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full text-stone hover:bg-canvas hover:text-ink"
        >
          <X size={14} />
        </button>
      )}
    </label>
  );
}

/** Debounced copy of a value (search boxes). */
export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function Card({
  title,
  actions,
  children,
  className = '',
  pad = true,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  pad?: boolean;
}) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <div className="flex min-h-[52px] items-center gap-3 border-b border-hairline px-4 py-3 sm:px-5">
          {title && <h2 className="text-[15px] font-bold tracking-[-0.01em]">{title}</h2>}
          {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={pad ? 'p-4 sm:p-5' : ''}>{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ Status */

type Tone = 'neutral' | 'coin' | 'mint' | 'berry' | 'sky' | 'sun' | 'ink';
// Text colours are the text-safe variants (≥4.5:1 on their tint) so 12px pills stay readable.
const TONES: Record<Tone, string> = {
  neutral: 'bg-ink/[0.06] text-graphite',
  coin: 'bg-coin-tint text-coin-ink',
  mint: 'bg-mint/15 text-mint-deep',
  berry: 'bg-berry/10 text-berry-deep',
  sky: 'bg-sky-500/12 text-sky-800',
  sun: 'bg-sun/30 text-ink',
  ink: 'bg-ink text-white',
};

const STATUS: Record<string, { label: string; tone: Tone }> = {
  // withdrawals
  requested: { label: 'Requested', tone: 'neutral' },
  risk_review: { label: 'Reviewing', tone: 'sky' },
  on_hold: { label: 'On hold', tone: 'coin' },
  approved: { label: 'Approved', tone: 'sky' },
  processing: { label: 'Sending', tone: 'sky' },
  paid: { label: 'Paid', tone: 'mint' },
  rejected: { label: 'Rejected', tone: 'berry' },
  failed: { label: 'Failed', tone: 'berry' },
  refunded: { label: 'Refunded', tone: 'neutral' },
  succeeded: { label: 'Succeeded', tone: 'mint' },
  // users
  active: { label: 'Active', tone: 'mint' },
  review: { label: 'In review', tone: 'sun' },
  restricted: { label: 'Restricted', tone: 'coin' },
  blocked: { label: 'Blocked', tone: 'berry' },
  deleted: { label: 'Deleted', tone: 'neutral' },
  // tickets
  open: { label: 'Open', tone: 'sky' },
  pending_staff: { label: 'Needs staff', tone: 'coin' },
  pending_user: { label: 'Waiting on user', tone: 'neutral' },
  closed: { label: 'Closed', tone: 'neutral' },
  // flags, holds
  confirmed: { label: 'Confirmed', tone: 'berry' },
  dismissed: { label: 'Dismissed', tone: 'neutral' },
  held: { label: 'Held', tone: 'coin' },
  released: { label: 'Released', tone: 'mint' },
  reversed: { label: 'Reversed', tone: 'berry' },
  // conversions
  credited: { label: 'Credited', tone: 'mint' },
  pending: { label: 'Pending', tone: 'sun' },
};

const sentence = (s: string) => {
  const t = s.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
};

export function Pill({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex h-5 items-center gap-1 whitespace-nowrap rounded-full px-2.5 text-[12px] font-bold leading-none ${TONES[tone]}`}
    >
      {children}
    </span>
  );
}

export function StatusPill({ status }: { status: string }) {
  const s = STATUS[status] ?? { label: sentence(status), tone: 'neutral' as Tone };
  return <Pill tone={s.tone}>{s.label}</Pill>;
}
export const statusLabel = (s: string) => STATUS[s]?.label ?? sentence(s);

const SEVERITY: Record<string, Tone> = {
  low: 'neutral',
  medium: 'sun',
  high: 'coin',
  critical: 'berry',
};
export function SeverityPill({ severity }: { severity: string }) {
  return <Pill tone={SEVERITY[severity] ?? 'neutral'}>{sentence(severity)}</Pill>;
}

/** 0–100 risk score as a short bar plus number. */
export function RiskMeter({ score }: { score: number | null }) {
  if (score === null)
    return (
      <span className="text-stone" aria-label="No risk score">
        —
      </span>
    );
  const tone =
    score >= 80 ? 'bg-berry' : score >= 50 ? 'bg-coin' : score >= 20 ? 'bg-sun' : 'bg-mint';
  return (
    <span
      className="inline-flex items-center gap-2"
      title={`Risk score ${score} of 100`}
      role="img"
      aria-label={`Risk score ${score} of 100`}
    >
      <span className="h-1.5 w-12 overflow-hidden rounded-full bg-ink/10">
        <span className={`block h-full ${tone}`} style={{ width: `${Math.max(4, score)}%` }} />
      </span>
      <span className="min-w-6 text-[13px] font-bold tabular-nums">{score}</span>
    </span>
  );
}

/* ------------------------------------------------------------------ States */

export function EmptyState({
  icon: Icon = Inbox,
  title,
  children,
  action,
}: {
  icon?: ComponentType<{ size?: number; className?: string }>;
  title: string;
  children?: ReactNode;
  /** One follow-up, e.g. a "Clear search" button. */
  action?: ReactNode;
}) {
  return (
    <div className="px-6 py-12 text-center sm:py-14">
      <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-canvas">
        <Icon size={20} className="text-stone" />
      </span>
      <div className="mt-3 text-[16px] font-bold">{title}</div>
      {children && (
        <p className="mx-auto mt-1 max-w-sm text-[14px] leading-relaxed text-stone">{children}</p>
      )}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function SkeletonRows({ rows = 6 }: { rows?: number }) {
  return (
    <div className="divide-y divide-hairline" role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-4 sm:px-5">
          <div className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-ink/[0.07]" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-1/3 animate-pulse rounded bg-ink/[0.07]" />
            <div className="h-3 w-1/2 animate-pulse rounded bg-ink/[0.05]" />
          </div>
          <div className="h-5 w-16 shrink-0 animate-pulse rounded-full bg-ink/[0.07]" />
        </div>
      ))}
    </div>
  );
}

export function ErrorNote({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null;
  return (
    <div
      role="alert"
      className="flex items-start gap-2.5 rounded-[12px] border border-berry/30 bg-berry/[0.06] px-4 py-3 text-[14px] text-berry-deep"
    >
      <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 break-words">
        {(error as Error).message || 'Something went wrong.'}
      </span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="-my-1 shrink-0 rounded-[8px] px-2.5 py-1 text-[13px] font-semibold text-ink underline decoration-berry/40 decoration-2 underline-offset-2 transition hover:bg-berry/10"
        >
          Try again
        </button>
      )}
    </div>
  );
}

/** A page the signed-in staff member's roles don't include (the API refuses it too). */
export function NoAccess({ need }: { need?: Permission }) {
  return (
    <div className="card">
      {need ? (
        <EmptyState icon={Lock} title="You don’t have access to this page">
          It needs “{PERMISSIONS[need].label}”. Ask whoever manages the team to add it to your
          roles.
        </EmptyState>
      ) : (
        <EmptyState icon={Lock} title="No access yet">
          You’re signed in, but your account doesn’t have a role. Ask whoever manages the team to
          give you one.
        </EmptyState>
      )}
    </div>
  );
}

/** Where actions would be, for someone whose roles let them look but not act. */
export function ViewOnly({ children }: { children: ReactNode }) {
  return (
    <p className="flex items-start gap-2 text-[13px] leading-snug text-stone">
      <Lock size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

/* ------------------------------------------------------------------ Small pieces */

export function Initial({ name, size = 36 }: { name: string | null; size?: number }) {
  // Stable hue per name so the same user always gets the same chip.
  let h = 0;
  for (const ch of name ?? '') h = (h * 31 + ch.charCodeAt(0)) % 360;
  return (
    <span
      aria-hidden="true"
      className="grid shrink-0 place-items-center rounded-full font-extrabold uppercase text-ink"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        background: `hsl(${h} 70% 88%)`,
      }}
    >
      {(name ?? '').trim()[0] ?? '?'}
    </span>
  );
}

/** The placeholder name a deleted account is left with ("deleted-ab12cd34"; chosen names can't
 *  contain "-"), for lists whose rows don't carry the account status. */
export const isDeletedName = (name: string | null | undefined) =>
  !!name && /^deleted-[0-9a-z]{8}$/.test(name);

/** Grey stand-in for the initial of an account its owner deleted. */
export function DeletedMark({ size = 36 }: { size?: number }) {
  return (
    <span
      aria-hidden="true"
      className="grid shrink-0 place-items-center rounded-full bg-ink/[0.07] text-stone"
      style={{ width: size, height: size }}
    >
      <UserX size={Math.round(size * 0.45)} strokeWidth={1.75} />
    </span>
  );
}

/**
 * A user in a list, linking to their profile. A deleted account reads "Deleted account" with its
 * pseudonymous id: its name and email were erased, and staff shouldn't see the placeholders as if
 * they were real.
 */
export function UserChip({
  id,
  username,
  sub,
  deleted = false,
}: {
  id: string;
  username: string | null;
  sub?: ReactNode;
  deleted?: boolean;
}) {
  const gone = deleted || isDeletedName(username);
  return (
    <Link
      to={`/users/${id}`}
      onClick={(e) => e.stopPropagation()}
      className="group flex min-w-0 items-center gap-2.5 rounded-[8px]"
    >
      {gone ? <DeletedMark size={32} /> : <Initial name={username} size={32} />}
      <span className="min-w-0">
        <span
          className={`block truncate text-[14px] font-bold group-hover:underline group-hover:decoration-coin group-hover:decoration-2 group-hover:underline-offset-2 ${gone ? 'text-graphite' : ''}`}
        >
          {gone ? 'Deleted account' : (username ?? 'Unknown user')}
        </span>
        {gone ? (
          <span className="block truncate font-mono text-[12px] text-stone">{id}</span>
        ) : (
          sub && <span className="block truncate text-[12px] text-stone">{sub}</span>
        )}
      </span>
    </Link>
  );
}

export function CopyButton({ text, label }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        } catch {
          toast('Couldn’t copy. Select the text instead.', 'error');
        }
      }}
      // The ::after pad gives the small icon a finger-sized hit area without changing layout.
      className="relative inline-flex items-center gap-1 rounded-[7px] px-1.5 py-0.5 text-[12px] font-semibold text-stone transition after:absolute after:-inset-1.5 after:content-[''] hover:bg-canvas hover:text-ink"
      title={done ? 'Copied' : `Copy ${label ?? text}`}
      aria-label={done ? 'Copied' : `Copy ${label ?? 'value'}`}
    >
      {done ? <Check size={13} className="text-mint-deep" /> : <Copy size={13} />}
      {label}
    </button>
  );
}

/** Monospace id with a copy button. */
export function Mono({ children }: { children: string }) {
  return (
    <span className="inline-flex items-center gap-0.5 align-middle">
      <code className="rounded-[6px] bg-canvas px-1.5 py-0.5 text-[12px] text-graphite">
        {children}
      </code>
      <CopyButton text={children} />
    </span>
  );
}

export function KV({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[minmax(96px,auto)_1fr] gap-x-4 gap-y-2.5 text-[14px] sm:gap-x-6">
      {items.map(([k, v], i) => (
        <div key={i} className="contents">
          <dt className="text-stone">{k}</dt>
          <dd className="min-w-0 break-words font-semibold">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function TimeAgo({ t }: { t: number | null | undefined }) {
  if (!t) return <span className="text-stone">—</span>;
  return (
    <time
      dateTime={new Date(t).toISOString()}
      title={new Date(t).toLocaleString()}
      className="whitespace-nowrap"
    >
      {relative(t)}
    </time>
  );
}
export function relative(t: number): string {
  const s = Math.round((Date.now() - t) / 1000);
  const future = s < 0;
  const a = Math.abs(s);
  const v =
    a < 45
      ? 'just now'
      : a < 3600
        ? `${Math.round(a / 60)}m`
        : a < 86400
          ? `${Math.round(a / 3600)}h`
          : a < 86400 * 30
            ? `${Math.round(a / 86400)}d`
            : new Date(t).toLocaleDateString(undefined, {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              });
  if (v === 'just now' || v.includes(' ')) return v;
  return future ? `in ${v}` : `${v} ago`;
}

/* ------------------------------------------------------------------ Focus helpers */

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';

/** Rendered and focusable-looking. checkVisibility() also sees closed <details> content, which
 *  Chrome hides with content-visibility while still reporting layout boxes. */
function isVisible(el: HTMLElement) {
  return typeof el.checkVisibility === 'function'
    ? el.checkVisibility()
    : el.getClientRects().length > 0;
}

/** Keep Tab inside `root` (dialogs, drawers). */
function trapTab(e: KeyboardEvent, root: HTMLElement) {
  if (e.key !== 'Tab') return;
  const els = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isVisible);
  if (els.length === 0) {
    e.preventDefault();
    root.focus();
    return;
  }
  const first = els[0]!;
  const last = els[els.length - 1]!;
  const active = document.activeElement;
  if (e.shiftKey && (active === first || active === root || !root.contains(active))) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && (active === last || !root.contains(active))) {
    e.preventDefault();
    first.focus();
  }
}

/** Send focus back to whatever opened an overlay, if it's still on the page. */
function restoreFocus(el: Element | null) {
  if (el instanceof HTMLElement && el !== document.body && el.isConnected && isVisible(el))
    el.focus({ preventScroll: true });
}

/* ------------------------------------------------------------------ Drawer */

export function Drawer({
  onClose,
  title,
  subtitle,
  headerRight,
  children,
  footer,
}: {
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  headerRight?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Pages pass a fresh onClose each render; keep the latest without re-running the effect (which
  // would steal focus back to the panel on every background refetch).
  const close = useRef(onClose);
  close.current = onClose;
  const opener = useRef<Element | null>(
    typeof document === 'undefined' ? null : document.activeElement,
  );
  // With the assistant open, the drawer sits beside it on wide screens.
  const beside = useAssistantOpen();
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      // A dialog on top handles its own keys, and the assistant beside the drawer its own.
      if (document.querySelector('[data-dialog-open]')) return;
      if ((e.target as Element | null)?.closest?.('[data-assistant]')) return;
      if (e.key === 'Escape') close.current();
      else if (ref.current) trapTab(e, ref.current);
    };
    window.addEventListener('keydown', onKey);
    ref.current?.focus();
    const from = opener.current;
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
      restoreFocus(from);
    };
  }, []);
  return (
    <div
      className={`fixed inset-0 z-40 flex justify-end bg-ink/40 ${beside ? 'lg:pr-[468px]' : ''}`}
      onMouseDown={(e) => e.target === e.currentTarget && close.current()}
    >
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex h-full w-full max-w-[720px] flex-col bg-canvas shadow-[-8px_0_32px_rgba(17,17,16,0.18)] outline-none"
        style={{ animation: 'drawer-in 200ms cubic-bezier(.2,.8,.3,1)' }}
      >
        <div className="flex items-start gap-3 border-b border-hairline bg-paper py-4 pl-4 pr-3 sm:pl-5 sm:pr-4">
          <div className="min-w-0 flex-1">
            <h2
              id={titleId}
              className="text-[18px] font-extrabold leading-snug tracking-[-0.02em] [overflow-wrap:anywhere]"
            >
              {title}
            </h2>
            {subtitle && <div className="mt-1 text-[13px] text-stone">{subtitle}</div>}
          </div>
          {headerRight && <div className="mt-[3px] shrink-0">{headerRight}</div>}
          <button
            type="button"
            onClick={() => close.current()}
            aria-label="Close"
            className="-mt-1.5 grid h-10 w-10 shrink-0 place-items-center rounded-full text-stone transition hover:bg-canvas hover:text-ink"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5 sm:py-5">
          {children}
        </div>
        {footer && (
          <div className="border-t border-hairline bg-paper px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 sm:px-5 sm:py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Dialogs (confirm / reason) */

export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  tone?: 'default' | 'danger' | 'coin';
  /** Small line above the title, e.g. "Step 1 of 2". */
  eyebrow?: string;
  /** Ask for a reason (`min` characters, 3 unless set); resolves with it. */
  reason?: {
    label: string;
    placeholder?: string;
    presets?: string[];
    initial?: string;
    min?: number;
  };
  /**
   * Make staff type something back before the button works, for sends that can't be undone (the
   * number of people a notification goes to). Compared on digits when `expect` is a number.
   */
  typed?: { label: string; expect: string; hint?: string };
  /** Ask for a whole number too (see askAmount). */
  amount?: {
    label: string;
    min: number;
    max: number;
    suffix?: string;
    hint?: (n: number) => string;
  };
}
/** What staff entered: the reason, the number, and what they typed back (digits only for a number). */
export type Answer = { reason: string; amount: number; typed: string };
type Pending = ConfirmOptions & { resolve: (v: Answer | null) => void; opener: HTMLElement | null };

let pending: Pending | null = null;
const dialogSubs = new Set<() => void>();
const emitDialog = () => dialogSubs.forEach((f) => f());

// The control that opened a dialog: focus goes back to it on close, and it shows the busy spinner
// (lib/press.ts) while the confirmed action runs, so a money decision never looks like it did nothing.
let lastPress: { el: HTMLElement; at: number } | null = null;
let lastOpener: HTMLElement | null = null;
if (typeof document !== 'undefined')
  document.addEventListener(
    'click',
    (e) => {
      const el = (e.target as Element | null)?.closest?.('button,summary,a[href]');
      if (el instanceof HTMLElement) lastPress = { el, at: Date.now() };
    },
    true,
  );
function currentOpener(): HTMLElement | null {
  const active = document.activeElement;
  let el: HTMLElement | null =
    lastPress && Date.now() - lastPress.at < 1500 && isVisible(lastPress.el)
      ? lastPress.el
      : active instanceof HTMLElement && active !== document.body
        ? active
        : null;
  // A dialog opened from another dialog's answer (two-step reject) belongs to the first opener.
  if (el?.closest('[data-dialog-open]')) el = pending?.opener ?? lastOpener;
  return el;
}
/** Re-arm lib/press.ts on the opener so the api() call that follows marks it busy. The synthetic
 *  click is stopped (and cancelled) before React sees it, so no handler runs twice. */
function relayPress(el: HTMLElement | null) {
  if (!el || el.tagName !== 'BUTTON' || !el.isConnected) return;
  const stop = (e: Event) => {
    e.stopPropagation();
    e.preventDefault();
  };
  document.body.addEventListener('click', stop, { capture: true });
  el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
  document.body.removeEventListener('click', stop, { capture: true });
}

/** Promise-based replacement for window.confirm/prompt. Resolves to the reason ('' when none asked) or null. */
export function confirmAction(opts: ConfirmOptions): Promise<string | null> {
  return ask(opts).then((a) => (a ? a.reason : null));
}
/** Same dialog with a number field; resolves to { amount, reason } or null. */
export function askAmount(
  opts: ConfirmOptions & { amount: NonNullable<ConfirmOptions['amount']> },
): Promise<Answer | null> {
  return ask(opts);
}
/** Same dialog, resolving to everything staff entered (the typed-back value included) or null. */
export function askDetails(opts: ConfirmOptions): Promise<Answer | null> {
  return ask(opts);
}
function ask(opts: ConfirmOptions): Promise<Answer | null> {
  return new Promise((resolve) => {
    const opener = currentOpener();
    pending?.resolve(null);
    pending = { ...opts, resolve, opener };
    emitDialog();
  });
}

export function DialogHost() {
  const d = useSyncExternalStore(
    (f) => (dialogSubs.add(f), () => dialogSubs.delete(f)),
    () => pending,
  );
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const ids = useId();
  useEffect(() => {
    setReason(d?.reason?.initial ?? '');
    setAmount('');
    setTyped('');
    setBusy(false);
  }, [d]);
  useEffect(() => {
    if (!d) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(null);
      else if (formRef.current) trapTab(e, formRef.current);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  const close = (v: Answer | null) => {
    lastOpener = d.opener;
    if (v) relayPress(d.opener);
    d.resolve(v);
    if (pending === d) pending = null;
    emitDialog();
    restoreFocus(d.opener);
  };
  const n = Number(amount);
  const amountOk = !d.amount || (/^\d+$/.test(amount) && n >= d.amount.min && n <= d.amount.max);
  const reasonOk = !d.reason || reason.trim().length >= (d.reason.min ?? 3);
  const digits = (s: string) => s.replace(/[^\d]/g, '');
  const numeric = !!d.typed && /^\d+$/.test(d.typed.expect);
  const typedOk =
    !d.typed || (numeric ? digits(typed) === d.typed.expect : typed.trim() === d.typed.expect);
  const ok = reasonOk && amountOk && typedOk;
  const btn =
    d.tone === 'danger'
      ? 'btn-primary !bg-berry-deep hover:!bg-[#a82015]'
      : d.tone === 'coin'
        ? 'btn-coin'
        : 'btn-primary';
  const presets = d.reason?.presets ?? [];
  // Long presets (full sentences for the user) read better as a list than as wrapped pills.
  const listPresets = presets.some((p) => p.length > 44);
  const range = d.amount
    ? `${d.amount.min.toLocaleString('en-US')}–${d.amount.max.toLocaleString('en-US')}`
    : '';
  return (
    <div
      data-dialog-open
      className="fixed inset-0 z-[60] grid place-items-center overflow-y-auto bg-ink/45 p-3 sm:p-4"
      onMouseDown={(e) => e.target === e.currentTarget && close(null)}
    >
      <form
        ref={formRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${ids}-t`}
        aria-describedby={d.body ? `${ids}-b` : undefined}
        className="w-full max-w-[460px] rounded-[20px] border-[1.5px] border-ink bg-paper p-5 shadow-[6px_6px_0_#111110] sm:p-6"
        style={{ animation: 'dialog-in 160ms ease-out' }}
        onSubmit={(e) => {
          e.preventDefault();
          if (!ok || busy) return;
          setBusy(true);
          close({
            reason: d.reason ? reason.trim() : '',
            amount: d.amount ? n : 0,
            typed: !d.typed ? '' : numeric ? digits(typed) : typed.trim(),
          });
        }}
      >
        {d.eyebrow && (
          <div className="mb-1.5 text-[12px] font-semibold text-stone">{d.eyebrow}</div>
        )}
        <h2
          id={`${ids}-t`}
          className="text-[20px] font-extrabold leading-tight tracking-[-0.02em] [overflow-wrap:anywhere]"
        >
          {d.title}
        </h2>
        {d.body && (
          <div id={`${ids}-b`} className="mt-2 text-[14px] leading-relaxed text-graphite">
            {d.body}
          </div>
        )}
        {d.amount && (
          <div className="mt-5">
            <label className="label" htmlFor={`${ids}-a`}>
              {d.amount.label}
            </label>
            <div className="relative">
              <input
                id={`${ids}-a`}
                autoFocus
                inputMode="numeric"
                autoComplete="off"
                aria-describedby={`${ids}-ah`}
                aria-invalid={amount !== '' && !amountOk}
                className="input pr-20 text-[18px] font-bold tabular-nums"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, '').slice(0, 9))}
              />
              {d.amount.suffix && (
                <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[14px] font-semibold text-stone">
                  {d.amount.suffix}
                </span>
              )}
            </div>
            <div
              id={`${ids}-ah`}
              className={`mt-1.5 text-[12px] tabular-nums ${amount && !amountOk ? 'font-semibold text-berry-deep' : 'text-stone'}`}
            >
              {amount && !amountOk
                ? `Enter a whole number between ${range}.`
                : amount && d.amount.hint
                  ? d.amount.hint(n)
                  : `Whole number, ${range}.`}
            </div>
          </div>
        )}
        {d.typed && (
          <div className="mt-5">
            <label className="label" htmlFor={`${ids}-c`}>
              {d.typed.label}
            </label>
            <input
              id={`${ids}-c`}
              autoFocus
              autoComplete="off"
              inputMode={/^\d+$/.test(d.typed.expect) ? 'numeric' : 'text'}
              aria-describedby={d.typed.hint ? `${ids}-ch` : undefined}
              aria-invalid={typed !== '' && !typedOk}
              className="input text-[18px] font-bold tabular-nums"
              value={typed}
              onChange={(e) => setTyped(e.target.value.slice(0, 40))}
            />
            {d.typed.hint && (
              <div id={`${ids}-ch`} className="mt-1.5 text-[12px] text-stone">
                {d.typed.hint}
              </div>
            )}
          </div>
        )}
        {d.reason && (
          <div className="mt-5">
            <label className="label" htmlFor={`${ids}-r`}>
              {d.reason.label}
            </label>
            {presets.length > 0 && (
              <div className={listPresets ? 'mb-2 grid gap-1.5' : 'mb-2 flex flex-wrap gap-1.5'}>
                {presets.map((p) => {
                  const on = reason === p;
                  return (
                    <button
                      key={p}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setReason(p)}
                      className={
                        listPresets
                          ? `flex items-start gap-2.5 rounded-[10px] border px-3 py-2.5 text-left text-[13px] leading-snug transition ${
                              on ? 'border-ink bg-canvas' : 'border-hairline hover:border-ink'
                            }`
                          : `min-h-8 rounded-full border px-3 py-1 text-[12px] font-semibold transition ${
                              on
                                ? 'border-ink bg-ink text-white'
                                : 'border-hairline hover:border-ink'
                            }`
                      }
                    >
                      {listPresets && (
                        <span
                          aria-hidden="true"
                          className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full border-[1.5px] ${
                            on ? 'border-ink bg-ink' : 'border-[#cfc8ba]'
                          }`}
                        >
                          {on && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                        </span>
                      )}
                      {p}
                    </button>
                  );
                })}
              </div>
            )}
            <textarea
              id={`${ids}-r`}
              autoFocus={!d.amount && !d.typed}
              className="input min-h-[88px] resize-y text-[14px] leading-relaxed"
              placeholder={d.reason.placeholder ?? 'At least a few words'}
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey))
                  e.currentTarget.form?.requestSubmit();
              }}
            />
            <div className="mt-1.5 flex justify-between gap-3 text-[12px] text-stone">
              {reasonOk ? (
                <span className="max-sm:hidden">Ctrl/⌘ + Enter to confirm</span>
              ) : (
                <span>
                  {presets.length
                    ? 'Pick one above or write your own.'
                    : 'Required: at least a few words.'}
                </span>
              )}
              {reason.length > 400 && <span className="tabular-nums">{reason.length}/500</span>}
            </div>
          </div>
        )}
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" className="btn-ghost" onClick={() => close(null)}>
            Cancel
          </button>
          <button
            type="submit"
            className={btn}
            disabled={!ok}
            autoFocus={!d.reason && !d.amount && !d.typed}
          >
            {d.confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------ Toasts */

interface Toast {
  id: number;
  text: string;
  tone: 'ok' | 'error';
}
let toasts: Toast[] = [];
const toastSubs = new Set<() => void>();
const emitToasts = () => toastSubs.forEach((f) => f());
let nextToast = 1;

export function toast(text: string, tone: Toast['tone'] = 'ok') {
  const t = { id: nextToast++, text, tone };
  toasts = [...toasts, t].slice(-4);
  emitToasts();
  setTimeout(
    () => {
      toasts = toasts.filter((x) => x.id !== t.id);
      emitToasts();
    },
    tone === 'error' ? 6000 : 3200,
  );
}
/** Run an admin action with a success toast, or an error toast with the server's message. */
export async function run<T>(fn: () => Promise<T>, ok?: string): Promise<T | undefined> {
  try {
    const r = await fn();
    if (ok) toast(ok);
    return r;
  } catch (e) {
    toast((e as Error).message || 'Something went wrong.', 'error');
    return undefined;
  }
}

export function Toaster() {
  const list = useSyncExternalStore(
    (f) => (toastSubs.add(f), () => toastSubs.delete(f)),
    () => toasts,
  );
  // Top of the screen, so a toast never covers the drawer footer or a dialog's buttons.
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed left-1/2 top-[max(12px,env(safe-area-inset-top))] z-[70] flex w-[min(calc(100vw-24px),420px)] -translate-x-1/2 flex-col gap-2"
    >
      {list.map((t) => (
        <div
          key={t.id}
          role={t.tone === 'error' ? 'alert' : undefined}
          className={`pointer-events-auto flex items-start gap-2.5 rounded-[14px] border-[1.5px] border-ink px-4 py-3 text-[14px] font-semibold shadow-[3px_3px_0_#111110] ${
            t.tone === 'error' ? 'bg-[#fff1ef] text-berry-deep' : 'bg-paper text-ink'
          }`}
          style={{ animation: 'toast-in 180ms ease-out' }}
        >
          {t.tone === 'ok' ? (
            <Check size={16} className="mt-0.5 shrink-0 text-mint-deep" aria-hidden="true" />
          ) : (
            <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          )}
          <span className="min-w-0 break-words">{t.text}</span>
        </div>
      ))}
    </div>
  );
}

/** Who did something: Claude, a staff member by name, a system job, or the user. */
export function actorLabel(actor: string, names: Record<string, string> = {}): string {
  if (actor.startsWith('ai:')) return 'Claude';
  if (actor.startsWith('stf_')) return names[actor] ?? 'Staff';
  if (actor.startsWith('usr_')) return 'User';
  if (actor.startsWith('email:')) return actor.slice(6);
  if (actor.startsWith('rule:')) return 'Risk rules';
  if (actor === 'system:wizzgift') return 'WizzGift';
  if (actor === 'system:cron') return 'Scheduler';
  if (actor.startsWith('system')) return 'System';
  return actor;
}

/* ------------------------------------------------------------------ Formatters */

export const fmtUsd = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
export const fmtNum = (n: number) => n.toLocaleString('en-US');
export function fmtMoney(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 ? 2 : 0,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}
